/**
 * App — the SPA root. On mount it first tries to RESTORE an existing signed session
 * (`lib/session`), so a refresh or a bookmarked deep link keeps the operator signed in; only when
 * there is no usable session does the passkey gate (AuthSurface) render BEFORE the shell. Once authenticated, the
 * AppShell + router render. Every one of the 12 sectors is now mounted via the unified route
 * composition (`sectorRoutes.tsx`, the Integrate-phase seam) — no ComingSoon placeholders remain.
 * `/` is the console home (the 14-service catalogue); every `/sN/...` deep link still resolves to
 * the page it always did, and unknown paths fall back to the console home.
 */
import { useEffect, useState, lazy, Suspense } from 'react';
/*
 * Imported from the MODULE, not the shared barrel. The barrel re-exports `AppShell`, so importing
 * `AuthSurface` through it would drag the whole Cloudscape console back into the pre-auth chunk
 * and undo the split below.
 */
import { AuthSurface, HeroFrame } from './components/shared/AuthSurface';
import { restoreSession } from './lib/session';

/*
 * The authenticated app is loaded ONLY once there is a principal. It used to be imported
 * statically, so the sign-in page downloaded the entire console — Cloudscape, all 58 sector pages,
 * every chart — before it could paint a button, none of which is usable until the passkey
 * assertion succeeds. This is the first-paint cost on the one surface every session starts at.
 */
const AuthedApp = lazy(() => import('./AuthedApp'));

/** The restoring frame, reused as the lazy-load fallback: both are "this page is settling". */
function Settling({ label }: { label: string }) {
  return (
    <HeroFrame
      action={
        <p className="hero__hint" role="status" aria-live="polite">
          {label}
        </p>
      }
    />
  );
}

export function App() {
  const [principalId, setPrincipalId] = useState<string | null>(null);
  // Start in the restoring state so a valid session never flashes the login screen on its way in.
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void restoreSession()
      .then((session) => {
        if (!cancelled && session) setPrincipalId(session.principalId);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (restoring) {
    /*
     * The SAME frame AuthSurface renders, not a lookalike. It used to be a hand-copied subset that
     * had drifted: no `<h1>`, so the document outline was empty for the whole restore window, and
     * `.app-shell__brand` — a class styled for the always-dark chrome bar, borrowed onto a surface
     * that now follows the theme. Sharing the frame makes restoring read as this page settling,
     * and makes that a structural guarantee rather than a thing to keep in sync by hand.
     *
     * Only the action slot differs: a status line where the sign-in control will be. It is not a
     * progress bar, because the restore has no measurable progress to report (R8).
     */
    return <Settling label="Checking for an existing session…" />;
  }

  if (!principalId) {
    return <AuthSurface onAuthenticated={setPrincipalId} />;
  }

  return (
    <Suspense fallback={<Settling label="Opening the console…" />}>
      <AuthedApp principalId={principalId} />
    </Suspense>
  );
}
